'use client'

import { useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useSearchParams } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  CalendarIcon,
  EyeIcon,
  MoreHorizontalIcon,
  SearchIcon,
  Trash2Icon,
  SquareMenuIcon,
  MonitorIcon,
  UserIcon,
  HashIcon,
  RefreshCwIcon
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger } from '@/components/ui/context-menu'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { DeleteConfirmationDialog, LoadingState, SortableHeader } from '@/components/partials'
import { SearchLogsFilters } from '@/components/partials/search-logs'
import { useSearchLogs, useDeleteSearchLog, useBulkDeleteSearchLogs } from '@/hooks'
import { useSearchLogsStore, useMeStore } from '@/store'
import { SearchLogsFilterParams } from '@/types/search-log'
import { SortEnum } from '@/enums'
import { formatDateTime } from '@/lib/utils'
import { hasPermission } from '@/lib/permissions'
import { BulkDeleteResponse } from '@/types/general'

const List = () => {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const { meState } = useMeStore()
  const currentUser = meState.response
  const canListUsers = hasPermission(currentUser, 'USERS', 'LIST')

  const [deleteSearchLogId, setDeleteSearchLogId] = useState<string | null>(null)
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState<boolean>(false)

  const selectedSearchLogIds = useSearchLogsStore(state => state.selectedSearchLogIds)
  const toggleSearchLogSelection = useSearchLogsStore(state => state.toggleSearchLogSelection)
  const selectAllSearchLogs = useSearchLogsStore(state => state.selectAllSearchLogs)
  const deselectAllSearchLogs = useSearchLogsStore(state => state.deselectAllSearchLogs)
  const isSearchLogSelected = useSearchLogsStore(state => state.isSearchLogSelected)

  const filters = useMemo<SearchLogsFilterParams>(() => {
    const params = new URLSearchParams(searchParams.toString())
    const userIdsParam = params.get('userIds')

    return {
      page: parseInt(params.get('page') || '1'),
      size: parseInt(params.get('size') || '20'),
      sort: (params.get('sort') as SortEnum) || SortEnum.DESC,
      sortBy: (params.get('sortBy') as SearchLogsFilterParams['sortBy']) || 'createdAt',
      q: params.get('q') || undefined,
      userIds: userIdsParam ? userIdsParam.split(',').filter(Boolean) : undefined,
      createdAtStart: params.get('createdAtStart') || undefined,
      createdAtEnd: params.get('createdAtEnd') || undefined
    }
  }, [searchParams])

  const { data, isLoading, error, refetch, isFetching } = useSearchLogs(filters)
  const { mutate: deleteSearchLogMutate, isPending: isDeleting } = useDeleteSearchLog()
  const { mutate: bulkDeleteMutate, isPending: isBulkDeleting } = useBulkDeleteSearchLogs()

  const totalPages = data?.pages ?? 1
  const currentPage = data?.page ?? filters.page ?? 1
  const pageSize = data?.size ?? filters.size ?? 20

  const updateUrlParams = (newFilters: Partial<SearchLogsFilterParams>) => {
    const params = new URLSearchParams()
    const updatedFilters = { ...filters, ...newFilters }

    Object.entries(updatedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            params.set(key, value.join(','))
          }
        } else {
          params.set(key, value.toString())
        }
      }
    })

    router.push(`?${params.toString()}`)
  }

  const handleFiltersChange = (newFilters: Partial<SearchLogsFilterParams>) => {
    updateUrlParams(newFilters)
  }

  const handleResetFilters = () => {
    router.push('/logs/search')
  }

  const handleSort = (sortBy: string, sort: SortEnum) => {
    handleFiltersChange({ sortBy: sortBy as SearchLogsFilterParams['sortBy'], sort, page: 1 })
  }

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page })
  }

  const handlePageSizeChange = (size: number) => {
    handleFiltersChange({ size, page: 1 })
  }

  const handleView = (id: string) => {
    router.push(`/logs/search/${id}`)
  }

  const handleDelete = (id: string) => {
    setDeleteSearchLogId(id)
  }

  const handleBulkDelete = () => {
    if (selectedSearchLogIds.length > 0) {
      setBulkDeleteConfirmOpen(true)
    }
  }

  const handleSelectAll = () => {
    if (data && data.items.length > 0) {
      if (selectedSearchLogIds.length === data.items.length) {
        deselectAllSearchLogs()
      } else {
        selectAllSearchLogs(data.items)
      }
    }
  }

  const confirmDeleteSearchLog = () => {
    if (deleteSearchLogId) {
      deleteSearchLogMutate(deleteSearchLogId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['search-logs'] })
          toast.success(t('deleted_successfully'))
          setDeleteSearchLogId(null)
        },
        onError: (error: any) => {
          if (!error.response?.data?.message) {
            toast.error(t('error_deleting'))
          }
          setDeleteSearchLogId(null)
        }
      })
    }
  }

  const confirmBulkDeleteSearchLogs = () => {
    if (selectedSearchLogIds.length > 0) {
      bulkDeleteMutate(selectedSearchLogIds, {
        onSuccess: async (response: BulkDeleteResponse) => {
          await queryClient.invalidateQueries({ queryKey: ['search-logs'] })
          if (response.deletedItems && response.deletedItems.length > 0) {
            toast.success(t('deleted_successfully'))
          }
          setBulkDeleteConfirmOpen(false)
        },
        onError: (error: any) => {
          if (error.response?.status === 422) {
            toast.error(error.response.data?.items?.ids || t('error_deleting'))
          } else {
            toast.error(t('error_deleting'))
          }
          setBulkDeleteConfirmOpen(false)
        }
      })
    }
  }

  const renderActionItems = (id: string, isDropdown = true) => {
    const ItemComponent = isDropdown ? DropdownMenuItem : ContextMenuItem

    return (
      <>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleView(id)
          }}>
          <EyeIcon className="h-4 w-4" />
          {t('search_log_details')}
        </ItemComponent>
        <ItemComponent
          onClick={e => {
            e.stopPropagation()
            handleDelete(id)
          }}
          className="text-destructive focus:text-destructive">
          <Trash2Icon className="h-4 w-4" />
          {t('delete')}
        </ItemComponent>
      </>
    )
  }

  const colSpan = canListUsers ? 7 : 6

  return (
    <>
      <Card>
        <CardHeader>
          <div className="grid gap-3 sm:flex sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <SearchIcon className="h-5 w-5" />
                {t('search_logs')}
              </CardTitle>
              <CardDescription>{t('search_logs_description')}</CardDescription>
            </div>
            <div className="flex items-center justify-end gap-3">
              <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isFetching || isLoading}>
                <RefreshCwIcon className={`h-4 w-4${isFetching ? 'animate-spin' : ''}`} />
              </Button>
              {selectedSearchLogIds.length > 0 && (
                <Button variant="destructive" onClick={handleBulkDelete} disabled={isBulkDeleting}>
                  <Trash2Icon className="h-4 w-4" />
                  <div className="hidden items-center gap-2 lg:flex">
                    <span>{t('delete_selected')}</span>
                    <span>({selectedSearchLogIds.length})</span>
                  </div>
                </Button>
              )}
              <SearchLogsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
                onResetFilters={handleResetFilters}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <LoadingState isLoading={isLoading} error={error?.message} onRetry={refetch} loadingRows={5}>
            {data && (
              <div className="space-y-4">
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12.5 text-center">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={data.items.length > 0 && selectedSearchLogIds.length === data.items.length}
                              onCheckedChange={handleSelectAll}
                              onClick={e => e.stopPropagation()}
                              className="cursor-pointer"
                            />
                          </div>
                        </TableHead>
                        {canListUsers && (
                          <TableHead className="min-w-40">
                            <SortableHeader
                              sortBy="userId"
                              currentSortBy={filters.sortBy}
                              currentSort={filters.sort}
                              onSort={handleSort}>
                              <UserIcon className="h-4 w-4" />
                              {t('user')}
                            </SortableHeader>
                          </TableHead>
                        )}
                        <TableHead className="min-w-48">
                          <SortableHeader
                            sortBy="q"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <SearchIcon className="h-4 w-4" />
                            {t('search_query')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-28 text-center">
                          <SortableHeader
                            sortBy="resultCount"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <HashIcon className="h-4 w-4" />
                            {t('result_count')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-30 text-center">
                          <SortableHeader
                            sortBy="ip"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <MonitorIcon className="h-4 w-4" />
                            {t('ip')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="min-w-40 text-center">
                          <SortableHeader
                            sortBy="createdAt"
                            currentSortBy={filters.sortBy}
                            currentSort={filters.sort}
                            onSort={handleSort}>
                            <CalendarIcon className="h-4 w-4" />
                            {t('searched_at')}
                          </SortableHeader>
                        </TableHead>
                        <TableHead className="w-25 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <SquareMenuIcon className="h-4 w-4" />
                            {t('actions')}
                          </div>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={colSpan} className="h-24 text-center">
                            {t('no_results')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.items.map((searchLog, index) => (
                          <ContextMenu key={searchLog.id}>
                            <ContextMenuTrigger asChild>
                              <TableRow
                                className={`hover:bg-muted/50 cursor-pointer transition-colors ${
                                  index % 2 === 0 ? 'bg-background' : 'bg-muted/25'
                                }`}
                                onClick={() => handleView(searchLog.id)}>
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center">
                                    <Checkbox
                                      checked={isSearchLogSelected(searchLog.id)}
                                      onCheckedChange={() => toggleSearchLogSelection(searchLog.id)}
                                      onClick={e => e.stopPropagation()}
                                      className="cursor-pointer"
                                    />
                                  </div>
                                </TableCell>
                                {canListUsers && (
                                  <TableCell>
                                    {searchLog.user ? (
                                      <div>
                                        <p className="font-medium">{searchLog.user.fullName}</p>
                                        <p className="text-muted-foreground text-xs">{searchLog.user.email}</p>
                                      </div>
                                    ) : (
                                      <span className="text-muted-foreground text-sm">{t('guest')}</span>
                                    )}
                                  </TableCell>
                                )}
                                <TableCell>
                                  <span className="text-sm">{searchLog.q || '-'}</span>
                                </TableCell>
                                <TableCell className="text-center font-mono text-sm">{searchLog.resultCount}</TableCell>
                                <TableCell className="text-center font-mono text-sm">{searchLog.ip}</TableCell>
                                <TableCell className="text-center">
                                  {formatDateTime(searchLog.createdAt, locale)}
                                </TableCell>
                                <TableCell className="text-center">
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={e => e.stopPropagation()}
                                        className="h-8 w-8 p-0">
                                        <MoreHorizontalIcon className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      {renderActionItems(searchLog.id, true)}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </TableCell>
                              </TableRow>
                            </ContextMenuTrigger>
                            <ContextMenuContent>{renderActionItems(searchLog.id, false)}</ContextMenuContent>
                          </ContextMenu>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
                <DataTablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={data.total}
                  pageSize={pageSize}
                  onPageChangeAction={handlePageChange}
                  onPageSizeChangeAction={handlePageSizeChange}
                />
              </div>
            )}
          </LoadingState>
        </CardContent>
      </Card>

      <DeleteConfirmationDialog
        open={!!deleteSearchLogId}
        onCancel={() => setDeleteSearchLogId(null)}
        onConfirm={confirmDeleteSearchLog}
        isDeleting={isDeleting}
      />

      <DeleteConfirmationDialog
        open={bulkDeleteConfirmOpen}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
        onConfirm={confirmBulkDeleteSearchLogs}
        isDeleting={isBulkDeleting}
      />
    </>
  )
}

export default List
